<template>
  <div
    :class="{ 'smooth-wrapper': useWrapper }"
    ref="refWrapper"
  >
    <div
      class="smooth-content"
      ref="refContent"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import Lenis from "lenis"

import "lenis/dist/lenis.css"

const route = useRoute()
const { $gsap, $ScrollTrigger } = useNuxtApp()
const { app } = useStores()

const props = defineProps({
  enabled: {
    default: true,
  },
  useWrapper: {
    default: false,
  },
  smooth: {
    default: 1,
  },
  pauseOnModal: {
    default: false,
  },
  isMainScroller: {
    default: false,
  },
})

const refWrapper = ref()
const refContent = ref()

// ************************************************************
// Init
// ************************************************************
const scroller = ref(null)
const init = function () {
  if (!props.enabled) return

  if (scroller.value) scroller.value.destroy()
  let opts = {}
  if (props.useWrapper) {
    opts.wrapper = refWrapper.value
    opts.content = refContent.value
  }
  scroller.value = new Lenis(opts)
  scroller.value.on("scroll", $ScrollTrigger.update)
  $gsap.ticker.add((time) => {
    scroller.value.raf(time * 1000)
  })
  $gsap.ticker.lagSmoothing(0)
  if (route.hash) {
    scroller.value.scrollTo(route.hash, { immediate: true })
  } else {
    scroller.value.scrollTo(0, { immediate: true })
  }

  if (props.isMainScroller) {
    app.setScroller(scroller.value)
  }

  if (props.paused) {
    scroller.value.stop()
  }
}

onMounted(init)
watch(() => props.useWrapper, init)
provide("scroller", scroller)

// ************************************************************
// Destroy
// ************************************************************
const destroy = function () {
  scroller.value?.destroy?.()
}

onBeforeUnmount(destroy)

// ************************************************************
// Content Height - Refresh ScrollTrigger
// ************************************************************
// const { height: bodyHeight } = useElementSize(window.body);
const { height: contentHeight } = useElementSize(refContent)
watch(
  () => contentHeight,
  () => {
    $ScrollTrigger.refresh()
  }
)

// ************************************************************
// Hash Link - Smooth scroll to hash
// ************************************************************
watch(
  () => route.hash,
  () => {
    scroller.value.scrollTo(route.hash)
  }
)

// ************************************************************
// Pause Scroll
// ************************************************************
watch(
  () => app.scrollPaused,
  (val) => {
    if (val) {
      scroller.value.stop()
    } else {
      scroller.value.start()
    }
  }
)

watch(
  () => [app.modalId, app.modalStoryId, props.pauseOnModal],
  () => {
    if (!props.pauseOnModal) return
    app.setScrollPaused(!!app.modalId || !!app.modalStoryId)
  }
)
</script>

<style lang="scss" scoped>
@import "@/styles/shared.scss";

.smooth-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>

<style lang="scss">
@import "@/styles/shared.scss";

@include desktop {
  .lenis.lenis-stopped {
    overflow: auto;
  }
}
</style>
